import type { Program, Step, Summary } from './types';
import convertMobiusSlugsToIds from '@/utils/mobius/convertMobiusSlugsToIds';
import getMobiusResponse from '@/utils/mobius/getMobiusResponse';
import { useDynamicExperienceContext } from '@/components/DynamicFlow/providers/DynamicExperienceProvider';

import type {
  Degree,
  Subject,
  Occupation,
} from '@/components/DesignSystem/Subsections/SalaryCalculator/types';

import { useContext, useEffect, useMemo, useState } from 'react';
import { getCareerPathwayData } from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/utils/getCareerPathwayData';
import { CareerPathwayData, Option } from './types';
import { MobiusContext } from '@/components/MobiusProvider';
import getTrackingContext from '@/utils/getTrackingContext';
import getDcsOptions from '@/utils/mobius/getDcsOptions';
import {
  ContextualDefaults,
  MobiusTrackingContext,
  MobiusProgram,
} from '@/types/Mobius';

import tagularEvent from '@/utils/tagular/tagularEvent';
import getUrlQuery from '@/utils/getUrlQuery';
import getTrafficSource from '@/utils/getTrafficSource';
import uuid from '@/utils/uuid';
import { useGlobalProps } from '@/components/GlobalContextProvider/GlobalContextProvider';
import getOccupations from '@/components/DynamicFlow/utils/data-fetch/getOccupations';
import useFlowTrackingEvents from '@/components/DynamicFlow/hooks/useFlowTrackingEvent';

const useCareerPathwayPlanner = (
  viewCorrelationId?: string,
): CareerPathwayData => {
  const { triggerProductClickedEvent } = useFlowTrackingEvents();
  const globalProps = useGlobalProps();
  const config = globalProps?.siteConfig;
  // Form state
  const [selectedCurrentEducation, setSelectedCurrentEducation] =
    useState<Degree | null>(null);
  const [selectedFieldOfStudy, setSelectedFieldOfStudy] =
    useState<Subject | null>(null);
  const [categories, setCategories] = useState<Option[]>([]);
  const [selectedCurrentRole, setSelectedCurrentRole] =
    useState<Occupation | null>(null);
  const [studyMatchDestination, setStudyMatchDestination] =
    useState<string>('');
  const [trackingContext, setTrackingContext] = useState<
    Partial<MobiusTrackingContext>
  >({
    formatSubtype: 'career-pathways-card',
    formatType: 'dexp-tool-results',
  });
  const [selectedTargetRole, setSelectedTargetRole] =
    useState<Occupation | null>(null);
  const [loading, setLoading] = useState(false);
  const [careerPlannerData, setCareerPlannerData] = useState<{
    steps: Step[];
    summary: Summary;
  } | null>(null);
  const [showPlanner, setShowPlanner] = useState(false);
  const [aidcs, setAidcs] = useState<String>('');

  const [estimatedTimeToComplete, setEstimatedTimeToComplete] =
    useState<string>('');
  const [averageSalaryIncrease, setAverageSalaryIncrease] =
    useState<string>('');
  const [loadingOccupations, setLoadingOccupations] = useState(false);
  const [currentRoleOptions, setCurrentRoleOptions] = useState<Occupation[]>(
    [],
  );
  const [targetRoleOptions, setTargetRoleOptions] = useState<Occupation[]>([]);
  const [mobiusPrograms, setMobiusPrograms] = useState<Program[]>([]);

  const { degrees, presets, siteConfig } = useContext(MobiusContext);

  const UNEMPLOYED_OPTION = {
    description: 'Unemployed',
    title: 'Unemployed',
    onetSocCode: '',
    salary: 0,
  };

  const STUDENT_OPTION = {
    description: 'Student',
    title: 'Student',
    onetSocCode: '',
    salary: 0,
  };

  const backupRoles = [
    {
      description: 'Healthcare',
      title: 'Healthcare',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Education',
      title: 'Education',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Business Administration',
      title: 'Business Administration',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Retail/Customer Service',
      title: 'Retail/Customer Service',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Skilled Trades',
      title: 'Skilled Trades',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Technology/IT',
      title: 'Technology/IT',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Transportation/Delivery',
      title: 'Transportation/Delivery',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Hospitality/Food Service',
      title: 'Hospitality/Food Service',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Construction/Manual Labor',
      title: 'Construction/Manual Labor',
      onetSocCode: '',
      salary: 0,
    },
    {
      description: 'Marketing/Communications',
      title: 'Marketing/Communications',
      onetSocCode: '',
      salary: 0,
    },
  ];

  // Generate and manage correlation ID - updates when Edit Details is clicked
  const [correlationId, setCorrelationId] = useState(() => uuid());

  const [productCorrelationId, setProductCorrelationId] = useState(() =>
    uuid(),
  );

  // Event context for dynamic experience events, using the flag to determine which ProductClicked event to trigger
  const { eventContext, currentStep, setClickedCtaLabel } =
    useDynamicExperienceContext();
  // Function to generate new correlationId when Edit Details is clicked
  const handleEditDetailsClick = () => {
    const newCorrelationId = uuid();
    setCorrelationId(newCorrelationId);
    return newCorrelationId;
  };

  const getOutboundUrl = (
    inventoryItem: MobiusProgram,
    trackingContext: MobiusTrackingContext,
    position: number,
    productCorrelationId: string,
  ): string => {
    const { destination } = inventoryItem.product;
    return getUrlQuery(destination.url, {
      correlationId: productCorrelationId,
      idToken: identityToken || '',
      helid: destination.helid,
      position,
      trackingContext: JSON.stringify(trackingContext),
      source: getTrafficSource(window.location.href),
    });
  };
  const { identityToken } = siteConfig;

  // Get options from DCS - replicating SalaryCalculator logic

  const options = useMemo(() => {
    if (degrees && degrees.length > 0 && presets) {
      try {
        const result = getDcsOptions(
          presets as ContextualDefaults,
          degrees as any,
        );
        return result;
      } catch (error) {
        console.error('Error getting DCS options:', error);
        return null;
      }
    } else {
      console.error('Missing requirements for getDcsOptions', degrees, presets);
      return null;
    }
  }, [degrees, presets]);

  const loadTrackingContext = async () => {
    try {
      const trackingTemp = trackingContext;
      const trackingContextWaitResult = await getTrackingContext(trackingTemp);
      setTrackingContext(trackingContextWaitResult);
      return trackingContextWaitResult;
    } catch (error) {
      console.error('Error loading tracking context:', error);
      return trackingContext;
    }
  };

  // Set initial values from presets (separate from category logic)
  useEffect(() => {
    // Set initial degree from presets
    if (presets?.degree && options?.degree) {
      const presetDegree = options.degree.find(
        (d: Degree) => d.slug === presets.degree,
      );
      if (presetDegree) {
        setSelectedCurrentEducation(presetDegree);
      }
    } else {
      console.error('Missing presets.degree or options.degrees');
    }
    // Set initial subject from presets
    if (presets?.subject && options?.subject) {
      const presetSubject = options.subject.find(
        (s) => s.slug === presets.subject,
      );
      if (presetSubject) {
        setSelectedFieldOfStudy(presetSubject);
      }
    }
  }, [options, presets]);

  // Fetch occupations on component mount
  useEffect(() => {
    const fetchOccupations = async () => {
      setLoadingOccupations(true);
      try {
        const occupationData = await getOccupations();

        // Check if occupationData is an array
        if (!Array.isArray(occupationData?.data)) {
          console.error('occupationData is not an array:', occupationData);
          setCurrentRoleOptions([
            ...backupRoles,
            UNEMPLOYED_OPTION,
            STUDENT_OPTION,
          ]);
          setTargetRoleOptions([
            ...backupRoles,
            UNEMPLOYED_OPTION,
            STUDENT_OPTION,
          ]);
          return;
        }

        // Map the response to just get the titles
        const mappedOccupations: Occupation[] = occupationData?.data?.map(
          (item: Occupation) => ({
            description: item.title || '',
            title: item.title || '',
            onetSocCode: item.onetSocCode || '',
            salary: item.salary || 0,
          }),
        );

        // Use the same data for both current and target roles
        // This will come from the occupation service
        setCurrentRoleOptions([
          ...mappedOccupations,
          UNEMPLOYED_OPTION,
          STUDENT_OPTION,
        ]);
        setTargetRoleOptions(mappedOccupations);
      } catch (error) {
        console.error('Error fetching occupations:', error);
        setCurrentRoleOptions([
          ...backupRoles,
          UNEMPLOYED_OPTION,
          STUDENT_OPTION,
        ]);
        setTargetRoleOptions([
          ...backupRoles,
          UNEMPLOYED_OPTION,
          STUDENT_OPTION,
        ]);
      } finally {
        setLoadingOccupations(false);
      }
    };

    fetchOccupations();
  }, []);

  // fetch categories and subjects
  useEffect(() => {
    const fetchCategoriesAndSubjects = async () => {
      if (!siteConfig?.identityToken) {
        console.error('No identity token available for fetching subjects');
        return;
      }

      try {
        const response = await getMobiusResponse(
          'degrees',
          siteConfig.identityToken,
        );

        if (response?.degrees) {
          const categories = response.degrees
            .find((degree) => degree.id === selectedCurrentEducation?.id)
            ?.categories.map((category) => ({
              id: category.id,
              name: category.name,
              nestedOptions: category.subject,
            }));

          setCategories(categories || []);
        }
      } catch (error) {
        console.error('Error fetching all subjects from Mobius:', error);
        // Fallback to context-based subjects if available
      }
    };
    fetchCategoriesAndSubjects();
  }, [selectedCurrentEducation, siteConfig]);

  useEffect(() => {
    if (careerPlannerData?.steps) {
      const firstStepWithDcs = careerPlannerData.steps.find(
        (step) => step?.dcs,
      );
      if (firstStepWithDcs) {
        const dcs = firstStepWithDcs.dcs;
        setAidcs(dcs);
      }
    }
  }, [careerPlannerData]);

  useEffect(() => {
    if (aidcs) {
      const parts = aidcs.split('+');

      if (parts.length >= 2) {
        const degree = parts[0]?.trim() || 'general';
        const category = parts[1]?.trim() || 'general';
        const subject = parts[2]?.trim() || 'general';

        const dcs = {
          degree,
          subject,
          category,
        };

        if (dcs && config) {
          setStudyMatchDestination(
            `${config?.studyMatchUrl}` +
              `?degree=${encodeURIComponent(degree)}` +
              `&category=${encodeURIComponent(category)}` +
              `&subject=${encodeURIComponent(subject)}`,
          );
        }
        if (presets && degrees) {
          // Fetch Mobius response after loading tracking context
          const type = 'programs';
          (async () => {
            try {
              const updatedTrackingContext = await loadTrackingContext();

              const convertedIds = convertMobiusSlugsToIds(dcs, dcs, degrees);
              const queryConfig = {
                criteria: {
                  ...convertedIds,
                  limit: 3,
                },
                trackingContext: updatedTrackingContext,
              };

              const response = await getMobiusResponse(
                type,
                identityToken,
                queryConfig,
              );
              if (response && Array.isArray(response)) {
                const programsData = response.map(
                  (item: any, index: number) => {
                    const itemCorrelationId = uuid();
                    return {
                      id: item.product?.program?.id || 0,
                      name: item.product?.program?.name || '',
                      category: item.product?.program?.category?.slug || '',
                      degree: item.product?.program?.degree?.slug || '',
                      subject: item.product?.program?.subject?.slug || '',
                      description:
                        item.product?.program?.description ||
                        item.product?.program?.snippet ||
                        '',
                      school: item.product?.school?.name || '',
                      destinationUrl: getOutboundUrl(
                        item,
                        updatedTrackingContext,
                        index + 1,
                        itemCorrelationId,
                      ),
                      logo: item.product?.school?.slug
                        ? `https://simple-storage-server.highereducation.com/${item.product?.school?.slug}.png`
                        : '',
                    };
                  },
                );
                setMobiusPrograms(programsData);
              }
            } catch (error) {
              console.error('Error fetching Mobius response:', error);
            }
          })();
        }
      }
    }
  }, [aidcs]);

  const programs = useMemo(() => {
    return mobiusPrograms.map((program) => ({
      id: program.id,
      subheader: program.school,
      title: program.name,
      description: program.description,
      isMonetizable: true,
      destinationUrl: program.destinationUrl,
      logo: program.logo,
      category: program.category,
      degree: program.degree,
      subject: program.subject,
    }));
  }, [mobiusPrograms]);

  const currentEducationOptions: Degree[] = useMemo(() => {
    return degrees || [];
  }, [degrees]);

  const steps: Step[] = useMemo(() => {
    return (
      careerPlannerData?.steps || [
        {
          title: "Obtain a Master's degree in business",
          description:
            'This will provide the necessary credentials and knowledge to advance in your field.',
          skills: ['Critical thinking', 'Academic writing', 'Research'],
          timeEstimate: '1 year',
          dcs: '',
        },
        {
          title: 'Gain relevant work experience',
          description:
            'Work in a marketing or business-related role to gain practical experience.',
          skills: ['Critical thinking', 'Academic writing', 'Research'],
          timeEstimate: '1 year',
          dcs: '',
        },
        {
          title: 'Network with industry professionals',
          description:
            'Attend industry events, join professional organizations, and connect with others in your field.',
          skills: ['Critical thinking', 'Academic writing', 'Research'],
          timeEstimate: '1 year',
          dcs: '',
        },
      ]
    );
  }, [careerPlannerData]);

  const makeFallbackMobiusCall = async () => {
    try {
      const updatedTrackingContext = await loadTrackingContext();

      const queryConfig = {
        criteria: {
          limit: 3,
        },
        trackingContext: updatedTrackingContext,
      };

      const response = await getMobiusResponse(
        'programs',
        identityToken,
        queryConfig,
      );

      if (response && Array.isArray(response)) {
        const programsData = response.map((item: any, index: number) => {
          const itemCorrelationId = uuid();
          return {
            id: item.product?.program?.id || 0,
            name: item.product?.program?.name || '',
            category: item.product?.program?.category?.slug || '',
            degree: item.product?.program?.degree?.slug || '',
            subject: item.product?.program?.subject?.slug || '',
            description:
              item.product?.program?.description ||
              item.product?.program?.snippet ||
              '',
            school: item.product?.school?.name || '',
            destinationUrl: getOutboundUrl(
              item,
              updatedTrackingContext,
              index + 1,
              itemCorrelationId,
            ),
            logo: item.product?.school?.slug
              ? `https://simple-storage-server.highereducation.com/${item.product?.school?.slug}.png`
              : '',
          };
        });
        setMobiusPrograms(programsData);
      }
    } catch (error) {
      console.error('Error in fallback Mobius call:', error);
    }
  };

  const handleSubmit = async (source?: string) => {
    if (
      !selectedCurrentEducation ||
      !selectedFieldOfStudy ||
      !selectedCurrentRole ||
      !selectedTargetRole
    ) {
      console.error('All fields must be selected');
      return;
    }

    // When Career Pathway is the dynamic flow entry step, set CTA label and advance so provider fires ProductClicked
    if (
      eventContext.dynamicExperienceEventFlag &&
      currentStep?.isEntry &&
      setClickedCtaLabel
    ) {
      setClickedCtaLabel('Discover My Path');
    }

    // Fire tagular event for button click
    try {
      if (!eventContext.dynamicExperienceEventFlag) {
        if (source === 'CareerDetails') {
          tagularEvent('ElementClicked', {
            correlationId: correlationId,
            webElement: {
              elementType: 'button',
              location: 'career-pathways',
              name: 'update-results-cta',
              text: 'update-details',
              position: 'career-pathways',
            },
          });
        } else {
          tagularEvent('ProductClicked', {
            correlationId: productCorrelationId,
            viewCorrelationId,
            product: {
              brand: 'null',
              category: 'null',
              formatSubtype: 'career-pathways-entry',
              formatType: 'dexp-tool-entry',
              location: 'career-pathways',
              name: 'null',
              positionEngine: 'null',
              variant: 'null',
            },
          });
        }
      } else {
        triggerProductClickedEvent({
          ...eventContext,
          customDimensions: [
            ...(eventContext.customDimensions || []),
            {
              key: 'ctaText',
              value: 'Discover My Path',
            },
          ],
        });
      }
    } catch (error) {
      console.error('Error firing tagular event:', error);
    }

    setLoading(true);
    try {
      const data = await getCareerPathwayData({
        currentEducation: selectedCurrentEducation,
        fieldOfStudy: selectedFieldOfStudy,
        currentRole: selectedCurrentRole,
        targetRole: selectedTargetRole,
      });
      setCareerPlannerData(data);
      setEstimatedTimeToComplete(data.summary.estimate);
      setAverageSalaryIncrease(data.summary.averageSalaryIncrease);
    } catch (error) {
      console.error('Error calling career pathway endpoint:', error);
      await makeFallbackMobiusCall();
    } finally {
      setLoading(false);
    }
  };

  return {
    categories,
    currentEducationOptions,
    currentRoleOptions,
    targetRoleOptions,
    steps,
    programs,
    selectedCurrentRole,
    selectedTargetRole,
    estimatedTimeToComplete,
    averageSalaryIncrease,
    // Form state
    selectedCurrentEducation,
    selectedFieldOfStudy,
    loading,
    // Form handlers
    setSelectedCurrentEducation,
    setSelectedFieldOfStudy,
    setSelectedCurrentRole,
    setSelectedTargetRole,
    handleSubmit,
    showPlanner,
    setShowPlanner,
    correlationId,
    productCorrelationId,
    handleEditDetailsClick,
    studyMatchDestination,
  };
};

export default useCareerPathwayPlanner;
